// Keyed tab routing for the Settings page.
//
// Tabs used to be identified by numeric index, which made inserting a new
// tab (e.g. Templates at position 3) a lockstep refactor of every caller
// across the app that passed `settingsTab: N`. Keys remove that landmine:
// adding or reordering tabs does not change any caller's routing.
//
// When adding a tab:
//   1. Add its key to SETTINGS_TAB_KEYS below.
//   2. Render its <Tab value={key} /> and <TabPanel index={key}> in
//      Settings.tsx in the desired visual order.
//   3. Navigation callers only care about the key, not the position.

export const SETTINGS_TAB_KEYS = [
  "profile",
  "integrations",
  "email",
  "templates",
  "botFlows",
  "security",
  "data",
  "systemGuide",
] as const;

export type SettingsTabKey = (typeof SETTINGS_TAB_KEYS)[number];

export const DEFAULT_SETTINGS_TAB: SettingsTabKey = "profile";

/**
 * Runtime guard: narrow an arbitrary string to a known settings tab key.
 * Useful when restoring persisted state that may be stale (e.g. a saved
 * "templates" key from an older build that has since been removed).
 */
export function isSettingsTabKey(value: unknown): value is SettingsTabKey {
  return (
    typeof value === "string" &&
    (SETTINGS_TAB_KEYS as ReadonlyArray<string>).includes(value)
  );
}
